import { useState } from 'react'
import type { ReactElement } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { withSessionRedirect } from '../lib/require-session.ts'
import { VaultGrantMatrix } from '../components/VaultGrantMatrix.tsx'
import { VaultNewRunnerForm } from '../components/VaultNewRunnerForm.tsx'
import {
  Callout,
  Checkbox,
  Chip,
  Code,
  DataTable,
  EmptyState,
  PageHeader,
} from '../components/ui/index.ts'
import type { Column } from '../components/ui/index.ts'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import {
  listBranchRules,
  listEnvironments,
  listGrants,
  listPrincipals,
  patchPrincipal,
} from '../lib/vault-api.ts'
import type {
  VaultBranchRuleRow,
  VaultEnvironmentRow,
  VaultGrantsOut,
  VaultPrincipalRow,
} from '../lib/vault-api.ts'

export const Route = createFileRoute('/vault/principals')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { principal?: string } => ({
    principal:
      typeof search.principal === 'string' ? search.principal : undefined,
  }),
  loaderDeps: ({ search }) => ({ principal: search.principal }),
  loader: async ({ deps }) =>
    withSessionRedirect(async () => {
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      if (!rootId)
        return {
          tree,
          rootId,
          principals: [] as VaultPrincipalRow[],
          environments: [] as VaultEnvironmentRow[],
          branchRules: [] as VaultBranchRuleRow[],
          principalId: null as string | null,
          grants: { env: [], item: [] },
        }

      const [principals, environments, branchRules] = await Promise.all([
        listPrincipals(rootId),
        listEnvironments(rootId),
        listBranchRules(rootId),
      ])
      const principalId = deps.principal ?? null
      const grants: VaultGrantsOut = principalId
        ? await listGrants(rootId, principalId)
        : { env: [], item: [] }

      return {
        tree,
        rootId,
        principals,
        environments,
        branchRules,
        principalId,
        grants,
      }
    }),
  component: VaultPrincipalsRoute,
})

function kindBadge(kind: VaultPrincipalRow['kind']): string {
  if (kind === 'agent_defaults') return 'all roles'
  return kind === 'agent_role' ? 'role' : kind
}

function VaultPrincipalsRoute(): ReactElement {
  const {
    tree,
    rootId,
    principals,
    environments,
    branchRules,
    principalId,
    grants,
  } = Route.useLoaderData()
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)

  async function refresh() {
    await router.invalidate()
  }

  async function toggleInherit(principal: VaultPrincipalRow) {
    setError(null)
    try {
      await patchPrincipal(rootId, principal.id, {
        inherit: !principal.inherit,
      })
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  const selected = principals.find((p) => p.id === principalId) ?? null

  const columns: Column<VaultPrincipalRow>[] = [
    {
      key: 'kind',
      header: 'kind',
      width: '8rem',
      // `neutral` for every kind: this says what a principal IS, not how
      // it is doing, and a coloured kind badge would compete with the
      // verified chip beside it for the operator's attention.
      render: (principal) => (
        <Chip tone="neutral">{kindBadge(principal.kind)}</Chip>
      ),
    },
    {
      key: 'name',
      header: 'name',
      // Selecting a principal is a navigation, and `DataTable`'s own
      // row-click would also fire when the inherit checkbox two cells
      // over is toggled, so the activator lives in this cell instead.
      // Not the `Button` primitive: the name is a monospace identifier
      // and `Code` carries its own `bg-sunken` well, which inside a
      // filled button reads as a box inside a box. Same treatment as
      // `VaultNodeTree`, which selects the same way.
      render: (principal) => (
        <button
          type="button"
          aria-current={principal.id === principalId ? 'true' : undefined}
          className={`rounded-sm text-left transition-colors hover:text-accent ${
            principal.id === principalId
              ? 'font-semibold text-accent'
              : 'text-fg'
          }`}
          onClick={() => {
            void router.navigate({
              to: '/vault/principals',
              search: { principal: principal.id },
            })
          }}
        >
          <Code>{principal.name}</Code>
        </button>
      ),
    },
    {
      key: 'verified',
      header: 'verified',
      width: '7rem',
      // An unverified principal is not a failure — plenty of them are
      // meant to stay unverified — so the quiet pair is `done`/`neutral`
      // rather than green/red.
      render: (principal) =>
        principal.verified ? (
          <Chip tone="done">yes</Chip>
        ) : (
          <Chip tone="neutral">no</Chip>
        ),
    },
    {
      key: 'inherit',
      header: 'inherit',
      align: 'center',
      width: '6rem',
      render: (principal) => (
        <Checkbox
          label={<span className="sr-only">inherit for {principal.name}</span>}
          checked={principal.inherit}
          disabled={principal.kind === 'agent_defaults'}
          onChange={() => {
            void toggleInherit(principal)
          }}
        />
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Principals & grants"
        description="Agent role defaults apply automatically to all current and future roles. Add shared capabilities there; use individual roles for exceptions."
      />
      {error !== null && <Callout tone="danger">{error}</Callout>}
      <VaultNewRunnerForm rootId={rootId} onCreated={refresh} />
      <DataTable
        columns={columns}
        rows={principals}
        getRowKey={(principal) => principal.id}
        selectedRowKey={principalId}
        caption="Vault principals for this root"
        empty={
          <EmptyState
            headline="No principals yet"
            description="A principal is what a grant hangs off — an agent role, a human operator, or a runner. Mint a runner with the form above to get the first one."
          />
        }
      />

      {selected && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-fg">
            Grants for <Code>{selected.name}</Code>
          </h2>
          {selected.kind === 'agent_role' && (
            <div className="mb-3">
              <Callout tone="info" title="Exceptions only">
                These are this role’s exceptions. Agent role defaults also
                apply.
              </Callout>
            </div>
          )}
          <VaultGrantMatrix
            rootId={rootId}
            principalId={selected.id}
            roots={tree.roots}
            environments={environments}
            envGrants={grants.env}
            itemGrants={grants.item}
            branchRules={branchRules}
            onChanged={refresh}
          />
        </section>
      )}
    </div>
  )
}
